import { reactive, readonly } from 'vue'
import UserConfigService from '@/core/services/UserConfigService'
import panelManager from '@/core/helpers/panelManager'
import store from '@/store'
import platformHandler from '@/core/context/platformHandler'
import { DOCK_ICONS } from './dockIcons'

const NAMESPACE = 'dock'

const MIN_ICON_SIZE = 32
const MAX_ICON_SIZE = 72
const DEFAULT_ICON_SIZE = 48
const MIN_MAG_SCALE = 1.2
const MAX_MAG_SCALE = 2.5
const DEFAULT_MAG_SCALE = 1.5


const state = reactive({
    autoHide: false,
    position: 'bottom',
    iconSize: DEFAULT_ICON_SIZE,
    magnification: true,
    magnificationScale: DEFAULT_MAG_SCALE,
    clickAnimation: true,
    bounceAnimation: true,
    bounceOnOpen: true,
    openPanelIds: [],
    pageItems: [],
    panelItems: [],
    allPageItems: [],
    allPanelItems: [],
})

let _unsubscribe = null
let _storeUnwatch = null

function loadState() {
    const saved = UserConfigService.get(NAMESPACE)
    if (saved.autoHide !== undefined) state.autoHide = saved.autoHide
    if (saved.position !== undefined) state.position = saved.position
    if (saved.iconSize !== undefined) state.iconSize = Math.min(MAX_ICON_SIZE, Math.max(MIN_ICON_SIZE, saved.iconSize))
    if (saved.magnification !== undefined) state.magnification = saved.magnification
    if (saved.magnificationScale !== undefined) state.magnificationScale = Math.min(MAX_MAG_SCALE, Math.max(MIN_MAG_SCALE, saved.magnificationScale))
    if (saved.clickAnimation !== undefined) state.clickAnimation = saved.clickAnimation
    if (saved.bounceAnimation !== undefined) state.bounceAnimation = saved.bounceAnimation
    if (saved.bounceOnOpen !== undefined) state.bounceOnOpen = saved.bounceOnOpen
}

function saveState() {
    const saved = UserConfigService.get(NAMESPACE)
    UserConfigService.set(NAMESPACE, {
        ...saved,
        autoHide: state.autoHide,
        position: state.position,
        iconSize: state.iconSize,
        magnification: state.magnification,
        magnificationScale: state.magnificationScale,
        clickAnimation: state.clickAnimation,
        bounceAnimation: state.bounceAnimation,
        bounceOnOpen: state.bounceOnOpen,
    })
}

function saveItemOrder() {
    const saved = UserConfigService.get(NAMESPACE)
    UserConfigService.set(NAMESPACE, {
        ...saved,
        autoHide: state.autoHide,
        iconSize: state.iconSize,
        pageOrder: state.pageItems.map(i => i.id),
        panelOrder: state.panelItems.map(i => i.id),
    })
}

function getModulesFromStore() {
    try {
        const data = platformHandler.getModulesData()
        if (Array.isArray(data) && data.length) return data
    } catch { void 0 }
    try {
        return JSON.parse(localStorage.getItem('company_modules') || '[]')
    } catch { return [] }
}

function getModuleItems() {
    const modules = getModulesFromStore()
    const codes = modules.map(m => m.code)
    const dockMap = platformHandler.getModuleDockMap()
    const dockKeys = Object.keys(dockMap)
    const pages = []
    const panels = []
    for (const code of codes) {
        const config = dockMap[code]
        if (!config) continue
        if (config.type === 'page') pages.push({ ...config, _dockOrder: dockKeys.indexOf(code) })
        else panels.push({ ...config, _dockOrder: dockKeys.indexOf(code) })
    }
    pages.sort((a, b) => a._dockOrder - b._dockOrder)
    panels.sort((a, b) => a._dockOrder - b._dockOrder)
    return { pages, panels }
}

function applyCustomOrder(allItems, savedOrder) {
    if (!savedOrder || !savedOrder.length) return allItems.slice()
    const map = {}
    allItems.forEach(item => { map[item.id] = item })
    const ordered = []
    for (const id of savedOrder) {
        if (map[id]) {
            ordered.push(map[id])
            delete map[id]
        }
    }
    for (const id in map) {
        ordered.push(map[id])
    }
    return ordered
}

function refreshDockItems() {
    const moduleItems = getModuleItems()
    const { pages: fixedPages, panels: fixedPanels } = platformHandler.getDockFixedItems()
    const allPages = [...fixedPages.map(i => ({ ...i })), ...moduleItems.pages]
    const allPanels = [...fixedPanels.map(i => ({ ...i })), ...moduleItems.panels]

    state.allPageItems = allPages
    state.allPanelItems = allPanels

    const saved = UserConfigService.get(NAMESPACE)
    const pageOrder = saved.pageOrder
    const panelOrder = saved.panelOrder

    if (pageOrder) {
        state.pageItems = applyCustomOrder(allPages, pageOrder)
    } else {
        state.pageItems = allPages.slice()
    }

    if (panelOrder) {
        state.panelItems = applyCustomOrder(allPanels, panelOrder)
    } else {
        state.panelItems = allPanels.slice()
    }
}

function syncOpenPanels() {
    const ids = panelManager.getMountedPanelIds()
    state.openPanelIds = ids.filter(id => panelManager.isPanelOpen(id))
}

function initPanelTracking() {
    if (_unsubscribe) return
    syncOpenPanels()
    _unsubscribe = panelManager.onPanelChange(() => {
        syncOpenPanels()
    })
}

function initStoreWatch() {
    if (_storeUnwatch) return
    const pathFn = platformHandler.getModulesStorePath()
    if (!pathFn) return
    try {
        _storeUnwatch = store.watch(
            pathFn,
            () => { refreshDockItems() },
            { deep: true }
        )
    } catch { void 0 }
}

function toggleAutoHide() {
    state.autoHide = !state.autoHide
    saveState()
}

function toggleMagnification() {
    state.magnification = !state.magnification
    saveState()
}

function setPosition(pos) {
    state.position = pos
    saveState()
}

function toggleClickAnimation() {
    state.clickAnimation = !state.clickAnimation
    saveState()
}

function toggleBounceAnimation() {
    state.bounceAnimation = !state.bounceAnimation
    saveState()
}

function toggleBounceOnOpen() {
    state.bounceOnOpen = !state.bounceOnOpen
    saveState()
}

function setMagnificationScale(scale) {
    state.magnificationScale = Math.round(Math.min(MAX_MAG_SCALE, Math.max(MIN_MAG_SCALE, scale)) * 10) / 10
    saveState()
}

function setIconSize(size) {
    state.iconSize = Math.round(Math.min(MAX_ICON_SIZE, Math.max(MIN_ICON_SIZE, size)))
    saveState()
}

function isPanelActive(panelId) {
    return state.openPanelIds.includes(panelId)
}

function isPanelMinimized(panelId) {
    const inst = panelManager.getPanelInstance(panelId)
    return inst?.minimized ?? false
}

function onPanelItemClick(panelId) {
    const inst = panelManager.getPanelInstance(panelId)
    if (!inst) return
    if (inst.minimized) {
        inst.restore()
    } else if (inst.visible) {
        if (inst.shakeHidden) {
            inst.shakeHidden = false
            panelManager.clearShakeHidden(panelId)
            return
        }
        if (inst.floatingMode) {
            inst.minimize()
        } else {
            inst.close()
        }
    } else {
        inst.open()
    }
}

function reorderItem(section, fromIndex, toIndex) {
    const list = section === 'page' ? state.pageItems : state.panelItems
    if (fromIndex < 0 || fromIndex >= list.length || toIndex < 0 || toIndex >= list.length) return
    const [item] = list.splice(fromIndex, 1)
    list.splice(toIndex, 0, item)
    saveItemOrder()
}

function removeItem(section, id) {
    const list = section === 'page' ? state.pageItems : state.panelItems
    const idx = list.findIndex(i => i.id === id)
    if (idx !== -1) list.splice(idx, 1)
    saveItemOrder()
}

function addItem(section, id) {
    const allList = section === 'page' ? state.allPageItems : state.allPanelItems
    const list = section === 'page' ? state.pageItems : state.panelItems
    if (list.find(i => i.id === id)) return
    const item = allList.find(i => i.id === id)
    if (item) {
        list.push({ ...item })
        saveItemOrder()
    }
}

function isItemInDock(section, id) {
    const list = section === 'page' ? state.pageItems : state.panelItems
    return !!list.find(i => i.id === id)
}

function getIcon(id) {
    return DOCK_ICONS[id] || ''
}

function closePanel(panelId) {
    const inst = panelManager.getPanelInstance(panelId)
    if (inst && typeof inst.close === 'function') inst.close()
}

function resetItemOrder() {
    const saved = UserConfigService.get(NAMESPACE)
    saved.pageOrder = null
    saved.panelOrder = null
    UserConfigService.set(NAMESPACE, saved)
    refreshDockItems()
}

function resetCustomizations() {
    state.iconSize = DEFAULT_ICON_SIZE
    state.magnification = true
    state.magnificationScale = DEFAULT_MAG_SCALE
    state.clickAnimation = true
    saveState()
}

export function useDockState() {
    loadState()
    refreshDockItems()
    initPanelTracking()
    initStoreWatch()
    platformHandler.loadModules().then(() => refreshDockItems()).catch(() => {})

    return {
        state: readonly(state),
        toggleAutoHide,
        toggleMagnification,
        setMagnificationScale,
        setIconSize,
        isPanelActive,
        isPanelMinimized,
        onPanelItemClick,
        reorderItem,
        removeItem,
        addItem,
        isItemInDock,
        saveItemOrder,
        refreshDockItems,
        getIcon,
        closePanel,
        setPosition,
        toggleClickAnimation,
        toggleBounceAnimation,
        toggleBounceOnOpen,
        resetItemOrder,
        resetCustomizations,
    }
}
